/// <reference types="@types/office-js" />

import { Injectable } from "@angular/core";
import { convert } from "html-to-text";
import dayjs from "dayjs";
import localizedFormat from "dayjs/plugin/localizedFormat";
import utc from "dayjs/plugin/utc";
dayjs.extend(localizedFormat);
import "dayjs/locale/nb";
import "dayjs/locale/cs";
import "dayjs/locale/de";
import "dayjs/locale/da";
import "dayjs/locale/es";
import "dayjs/locale/fi";
import "dayjs/locale/fr";
import "dayjs/locale/it";
import "dayjs/locale/nl";
import "dayjs/locale/pl";
import "dayjs/locale/sv";
import "dayjs/locale/br";
import "dayjs/locale/vi";
import "dayjs/locale/ko";
import "dayjs/locale/zh";

import { ResourceService } from "./resource.service";

import { IBlade } from "../model/blade.model";
import { IPreferencesDialogstate } from "../model/dialog-state.model";
import { Capability, ICapabilitiesPrivileged } from "../model/shared.model";
import { CompanyDefaultAvatarUrl, PersonDefaultAvatarUrl, UnknownAvatarUrl } from "src/app/utilities/ImageUrl";
import { IContactPrefillRequest, IFindSenderByMail } from "../model/contact-or-person.model";
import { IArchiveStatus } from "src/app/model/dialog-state.model";
import { OutlookService } from "./outlook/outlook.service";
import { from, Observable } from "rxjs";
import { LoadedContextService } from "./loadedContext.service";
import { SharedMailboxService } from "./shared-mailbox.service";

@Injectable({
  providedIn: "root",
})
export class SharedService {
  constructor(
    public readonly resource: ResourceService,
    public readonly outlookService: OutlookService,
    public readonly loadedContextService: LoadedContextService,
    private readonly sharedMailboxService: SharedMailboxService
  ) { }

  get currentSelectedTab(): SelectedTab {
    return this.currSelectedTab;
  }

  set currentSelectedTab(value: SelectedTab) {
    this.currSelectedTab = value;
  }

  get officeMailbox(): Office.Mailbox {
    return this.outlookService.mailbox;
  }

  get officeUserId(): string {
    return SharedService.normalizeOfficeUserId(
      this.outlookService.userProfile.emailAddress
    );
  }

  get officeHostName(): string {
    return this.outlookService.mailbox.diagnostics.hostName;
  }

  get isNAASupported(): boolean {
    return Office.context.requirements.isSetSupported("NestedAppAuth");
  }

  /**
   * MAC to get internetMessageId with < starting & ending >
   */
  get officeMailboxInternetMessageId(): string {
    const uniqueId = this.outlookService.internetMessageId;
    if (!uniqueId || uniqueId.trim() === "") {
      throw new Error("email unique id is empty...!");
    }
    return uniqueId.charAt(0) === "<" ? uniqueId : "<" + uniqueId + ">";
  }

  get officeEventType(): typeof Office.EventType {
    // extracting this type at runtime allows us to unit-test methods which depends on this
    // during setup via setting values in mocks.
    return Office.EventType;
  }

  get isCurrentEmailOutgoing(): boolean {
    const fromAddress = this.outlookService.mailbox.item.from?.emailAddress;
    if (SharedService.isOutgoingEmail(this.outlookService.userProfile.emailAddress, fromAddress)) {
      return true;
    }
    // Shared mailbox sent items have From = shared mailbox address, not the user's primary address.
    return this.sharedMailboxService.isSharedMailbox
      && SharedService.isOutgoingEmail(this.sharedMailboxService.targetMailbox, fromAddress);
  }

  isMac(): boolean {
    return Office.context.platform === Office.PlatformType.Mac;
  }

  isPC(): boolean {
    return Office.context.platform === Office.PlatformType.PC;
  }

  isRunningOnDesktop(): boolean {
    return this.isMac() || this.isPC();
  }

  getEntrypointUrl(): string {
    if (this.loadedContextService.isComposeAppointment()) {
      return "/compose/compose-appointment"
    } else if (this.loadedContextService.isReadAppointment()) {
      return "/appointment";
    } else if (this.loadedContextService.isComposeMode()) {
      return "/compose/compose-archive"
    } else {
      if (this.currentSelectedTab === SelectedTab.People) {
        return "/inbox/people";
      } else {
        return "/inbox/action";
      }
    }
  }

  private userImage = null;
  private currSelectedTab: SelectedTab = SelectedTab.Action;

  public currentPreferencesDialogState: IPreferencesDialogstate;

  public currentCapabilitiesPrivileged: ICapabilitiesPrivileged[] = [];

  public knownContactsForEmail: IFindSenderByMail[];

  public currentlySelectedEmailId: string;

  public blade: IBlade;

  public currentRecipients: IFindSenderByMail[];

  public currentRecipientShowMoreStatus: boolean;

  static normalizeOfficeUserId(emailAddress: string): string {
    if (!emailAddress || emailAddress.trim() === "") {
      throw new Error("Missing email-address. Unable to create user-id!");
    }

    return btoa(emailAddress.toLowerCase());
  }

  static isOutgoingEmail(
    accountAddress: string,
    emailAddress: string
  ): boolean {
    if (!accountAddress || !emailAddress) {
      return false;
    }
    const lowerAccount = accountAddress.toLowerCase();
    const lowerEmail = emailAddress.toLowerCase();
    return lowerAccount === lowerEmail;
  }

  /**
   * REST id resolved via Graph when Office.js fails to provide an itemId
   * (S/MIME signed emails on Outlook for Mac expose itemId === "").
   * Keyed by internetMessageId so it self-invalidates when the selected email changes.
   */
  private itemRestIdFallback: { internetMessageId: string; restId: string } | null = null;

  public setItemRestIdFallback(internetMessageId: string, restId: string): void {
    this.itemRestIdFallback = { internetMessageId, restId };
  }

  private getItemRestIdFallback(): string | null {
    if (!this.itemRestIdFallback) {
      return null;
    }
    const currentInternetMessageId = this.outlookService.mailboxItem?.internetMessageId;
    return this.itemRestIdFallback.internetMessageId === currentInternetMessageId
      ? this.itemRestIdFallback.restId
      : null;
  }

  /**
   * Get REST friendly itemId (mail Id)
   */
  public getItemRestId(): string {
    const itemId = this.outlookService.itemId;
    if (!itemId) {
      // Graph-resolved ids are already REST-formatted; no EWS conversion needed.
      const fallbackRestId = this.getItemRestIdFallback();
      if (fallbackRestId) {
        return fallbackRestId;
      }
      throw new Error("Unable to archive email without email-id!");
    }
    const hostName = this.outlookService.mailbox.diagnostics.hostName;
    if (hostName === "OutlookIOS" || hostName === "OutlookAndroid") {
      // itemId is already REST-formatted
      return itemId;
    } else {
      // Convert to an item ID for API v2.0
      return this.convertEwsID(itemId);
    }
  }

  /**
  * Get REST friendly attachmentId (attachment Id)
  */
  public getAttachmentRestId(attachmentId: string): string {
    if (!attachmentId) {
      throw new Error("Unable to archive attachment without attachment-id!");
    }
    const hostName = this.outlookService.mailbox.diagnostics.hostName;
    if (hostName === "OutlookIOS" || hostName === "OutlookAndroid") {
      // attachmentId is already REST-formatted
      return attachmentId;
    } else {
      // Convert to an attachment ID for API v2.0
      return this.convertEwsID(attachmentId);
    }
  }

  /**
   * https://docs.microsoft.com/en-us/office/dev/add-ins/reference/objectmodel...
   * .../requirement-set-1.5/office.context.mailbox#converttorestiditemid-restversion--string
   * This method is not supported in Outlook on iOS or Android.
   *
   * @param itemId - Unique ID for each mail
   */
  public convertEwsID(itemId: string): string {
    return this.outlookService.convertToRestId(itemId);
  }

  /**
 * This will convert an id (OfficeJS ID) to a Microsoft Outlook Rest API accepted id format
 *
 * @param itemID Office ID
 */
  public convertIDsToOutlookRestApiIDFormat(itemID: string): string {
    const chars = { "/": "-", _: "+" };
    return itemID.replace(/[/_]/g, (m) => chars[m]);
  }

  private convertHtmlToAdjustedPlainText(html: string): string {
    const div = document.createElement("div");
    div.innerHTML = html;
    // Replace <div><br></div> tags that Outlook adds with simple <br>
    const elements = div.getElementsByTagName("div");
    for (const element of Array.from(elements)) {
      if (element.childNodes.length === 1 && element.childNodes[0].nodeName === "BR") {
        element.parentNode.replaceChild(document.createElement("br"), element);
      }
    }

    const converted = convert(div.innerHTML, {
      selectors: [
        { selector: "img", format: "skip" },
        { selector: "p", options: { leadingLineBreaks: 1, trailingLineBreaks: 1 } },
        { selector: "pre", options: { leadingLineBreaks: 1, trailingLineBreaks: 1 } },
      ]
    });
    return converted;
  }

  public getPlainTextMailBody(): Observable<string> {
    return from(new Promise<string>((resolve, reject) => {
      this.officeMailbox.item.body.getAsync(Office.CoercionType.Html, { asyncContext: null }, (result) => {
        if (result.status === Office.AsyncResultStatus.Succeeded) {
          const plainText = this.convertHtmlToAdjustedPlainText(result.value);
          resolve(plainText);
        } else {
          reject(result.error);
        }
      });
    }));
  }

  /**
   * Merge the unknown recipients with known ones for CRM
   *
   * @param recipients Get the mail to, cc & bcc email id's
   * @param people Get the information received for recipients
   */
  public includeUnknownRecipients(
    recipients: Office.EmailAddressDetails[],
    people: IFindSenderByMail[]
  ) {
    try {
      recipients.forEach((recipient) => {
        if (
          !people.find((item) => item.EmailAddress === recipient.emailAddress)
        ) {
          const oPeople = {
            PersonId: 0,
            FullName: recipient.displayName,
            Title: null,
            EmailAddress: recipient.emailAddress,
            ContactId: 0,
            ContactName: null,
            ContactDepartment: null,
            ContactCategory: null,
            Telephone: null,
            MobilePhone: null,
            OurContactName: null,
            ImageUrl: null,
            ImageData: null,
            SortName: null,
          } as IFindSenderByMail;

          people.push(oPeople);
        }
      });

      return people.sort((a, b) => (a.FullName < b.FullName ? -1 : 1));
    } catch {
      return null;
    }
  }

  public hasCapability(name: Capability): boolean {
    return this.currentCapabilitiesPrivileged.some(c => c.Name === name);
  }

  /**
   * This method converts the datetime to appropriate browser language datetime
   *
   * @param dt DateTime Ex: 2019-07-19T05:34:47Z
   */
  public getDateTimeConvertedToBrowserLanguage(dt: string) {
    dayjs.locale(this.getUiLang());
    return dayjs(dt).format("L, LTS");
  }

  public resolveLocalDate(archiveStatus: IArchiveStatus): string {
    dayjs.extend(utc);
    dayjs.locale(this.getUiLang());

    if (!archiveStatus.ArchivedAtUtc) {
      return dayjs.utc(archiveStatus.ArchivedAt).format("L");
    }
    return dayjs.utc(archiveStatus.ArchivedAtUtc).local().format("L");
  }

  public resolveLocalDateFromDate(date: Date): string {
    dayjs.extend(utc);
    dayjs.locale(this.getUiLang());

    if (!date) {
      return dayjs.utc(date).format("L");
    }
    return dayjs.utc(date).local().format("L");
  }

  public getDateConvertedToBrowserLanguage(dt: string) {
    const uiLang =
      this.resource.currentUILanguage() === "NO"
        ? "NB"
        : this.resource.currentUILanguage();
    dayjs.locale(uiLang);
    return dayjs(dt).format("L");
  }

  public setUserProfileImg(userProfileImg: string) {
    this.userImage = userProfileImg;
  }

  public getUserProfileImg() {
    return this.userImage;
  }

  public isOutlookIOS(): boolean {
    return this.officeHostName === "OutlookIOS";
  }

  public isOutlookAndroid(): boolean {
    return this.officeHostName === "OutlookAndroid";
  }

  /**
   * The contact prefill request for the person being added, or null when no suggestion is offered:
   * only in read mode (sender or recipient), where the person can have a message of their own in the conversation.
   */
  public contactPrefillRequestFor(audience: string, emailAddress: string, displayName: string): IContactPrefillRequest | null {
    if (audience !== "sender" && audience !== "recipient") {
      return null;
    }

    const conversationId = this.outlookService.mailboxItem?.conversationId;
    if (!conversationId || !emailAddress || !this.isHugoAiGptEnabled()) {
      return null;
    }

    return { conversationId, emailAddress, displayName: displayName ?? "", role: audience };
  }

  public supportsPersonDetails(): boolean {
    const capability = this.currentCapabilitiesPrivileged.find(c => c.Name === Capability.SupportsPersonDetails);
    return capability ? Boolean(JSON.parse(capability.Description)) : false;
  }

  public isHugoAiGptEnabled(): boolean {
    return this.hasCapability(Capability.HugoAiGpt);
  }

  public setupBlade(
    senderName: string,
    senderEmail: string,
    mailSender: IFindSenderByMail[]
  ): void {
    this.blade = {
      name: senderName,
      email: senderEmail,
      companyId: mailSender.length === 1 ? mailSender[0].ContactId : 0,
      companyName: mailSender.length === 1 ? mailSender[0].ContactName : null,
      contactId: mailSender.length === 1 ? mailSender[0].PersonId : 0,
      contactName: mailSender.length === 1 ? mailSender[0].FullName : null,
      image: this.getImage(mailSender),
      activeUser:
        mailSender.length === 0
          ? MailSenderStatus.ZeroContactsBySenderMail
          : mailSender.length > 1
            ? MailSenderStatus.ManyContactsBySenderMail
            : MailSenderStatus.SingleContactBySenderMail,
    } as IBlade;
  }

  getImage(mailSender: IFindSenderByMail[]): string {
    if (!mailSender || mailSender.length !== 1) {
      return UnknownAvatarUrl;
    }

    // is it a person and known to CRM ?
    if (mailSender[0].PersonId > 0) {
      return mailSender[0].ImageData
        ? mailSender[0].ImageData
        : PersonDefaultAvatarUrl;
    }

    // Company
    return CompanyDefaultAvatarUrl;
  }

  /**
    * This method replaces existing item in the currentRecipients array.
    *
    * @param mailRecipient IFindSenderByMail
    */
  public updateCurrentRecipients(mailRecipient: IFindSenderByMail): void {
    const idx = this.currentRecipients.findIndex((x) => x.EmailAddress === mailRecipient.EmailAddress);
    if (idx !== -1) {
      this.currentRecipients[idx] = mailRecipient;
    }
  }

  getEmailDetailsForCurrentEmail(): Office.EmailAddressDetails {
    const recipients = this.getAllEmailRecpieintsForCurrentEmail();
    const isOutgoing = this.isCurrentEmailOutgoing;

    // item.from is undefined in compose mode — fall back to first recipient or null.
    const emailDetails = isOutgoing && recipients.length > 0
      ? recipients[0]
      : (this.officeMailbox.item.from ?? recipients[0] ?? null);

    return emailDetails;
  }

  getAllEmailRecpieintsForCurrentEmail(): Office.EmailAddressDetails[] {
    const to = this.officeMailbox.item.to;
    const cc = this.officeMailbox.item.cc;

    // In compose mode, to/cc are Office.Recipients objects (not arrays) — skip them.
    const toArray = Array.isArray(to) ? to : [];
    const ccArray = Array.isArray(cc) ? cc : [];
    const recipients = [...new Set([...toArray, ...ccArray])].filter(Boolean);
    return recipients;
  }

  private getUiLang(): string {
    return this.resource.currentUILanguage() === "NO" ? "NB" : this.resource.currentUILanguage();
  }
}

export enum MailSenderStatus {
  ZeroContactsBySenderMail = 0,
  SingleContactBySenderMail = 1,
  ManyContactsBySenderMail = 2,
}

export enum Entity {
  Company = "Contacts",
  Contact = "Persons",
}

export enum SaveContactActions {
  CreateNew = 0,
  AddEMail = 1,
  AddToCompany = 2,
}

export enum States {
  PreferencesDialogState = "PreferencesDialogState",
  DocumentsDialogState = "DocumentsDialogState",
  CapabilitiesPrivileged = "CapabilitiesPrivileged",
}

export enum SelectedTab {
  Action,
  People,
}
